import React, { useState, useRef } from 'react';
import type {
  ItineraryV2 as Itinerary,
  DayV2 as Day,
  ItemV2 as Item,
} from '@cubemeet/core/modules/travel/itineraryV2/types';

export interface UseItineraryEditorOptions {
  initialItinerary?: any;
  initialFormData?: any;
  expectedDays?: number;
}

export const genUniqueId = () => `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

// 确保有足够的天数
const ensureDaysCount = (it: Itinerary, expectedDays?: number, startDate?: Date | null): Itinerary => {
  const originalDays = Array.isArray(it.days) ? it.days : [];

  // 將現有資料按照 day 編號聚合，避免重複和順序混亂
  const dayBuckets = new Map<number, Day>();
  let fallbackCounter = 1;
  let maxDayNumber = 0;

  originalDays.forEach((dayEntry) => {
    const rawDay = typeof dayEntry?.day === 'number' && dayEntry.day > 0 ? dayEntry.day : null;
    const dayNumber = rawDay ?? fallbackCounter++;
    maxDayNumber = Math.max(maxDayNumber, dayNumber);

    const current = dayBuckets.get(dayNumber);
    const items = Array.isArray(dayEntry?.items) ? dayEntry.items : [];
    const mergedItems = current ? [...(current.items || []), ...items] : items;

    dayBuckets.set(dayNumber, {
      ...(current || {}),
      ...(dayEntry || {}),
      day: dayNumber,
      date: dayEntry?.date || current?.date,
      items: mergedItems,
    } as Day);
  });

  const targetCount = Math.max(
    expectedDays || 0,
    dayBuckets.size,
    maxDayNumber,
  );

  const resultDays: Day[] = [];
  const baseDate = startDate && !Number.isNaN(startDate.getTime()) ? new Date(startDate) : null;
  const pad = (n: number) => String(n).padStart(2, '0');

  for (let i = 1; i <= targetCount; i++) {
    const existing = dayBuckets.get(i);
    let date = existing?.date;
    
    // If baseDate is provided, strictly enforce the date sequence (Day N = Start + N-1)
    // This fixes issues where days might have incorrect dates from previous edits or sparse arrays
    if (baseDate) {
      const dt = new Date(baseDate);
      dt.setDate(dt.getDate() + (i - 1));
      date = `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
    } else if (!date && baseDate) {
      const dt = new Date(baseDate);
      dt.setDate(dt.getDate() + (i - 1));
      date = `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
    }

    resultDays.push({
      day: i,
      date,
      items: existing?.items ? existing.items : [],
    } as Day);
  }

  console.log('[ensureDaysCount] resultDays:', resultDays.map((d) => ({ day: d.day, date: d.date, items: d.items?.length || 0 })));

  return {
    title: it.title,
    location: it.location,
    days: resultDays,
  };
};

// 从表单数据计算天数
const buildDaysFromForm = (form: any, daysCount?: number) => {
  try {
    const startRaw = form?.startDate || form?.start_date || form?.start;
    const endRaw = form?.endDate || form?.end_date || form?.end;
    
    let start: Date | null = null;
    if (startRaw) {
       if (typeof startRaw === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(startRaw)) {
         const [y, m, d] = startRaw.split('-').map(Number);
         start = new Date(y, m - 1, d);
       } else {
         start = new Date(startRaw);
       }
    }

    let end: Date | null = null;
    if (endRaw) {
       if (typeof endRaw === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(endRaw)) {
         const [y, m, d] = endRaw.split('-').map(Number);
         end = new Date(y, m - 1, d);
       } else {
         end = new Date(endRaw);
       }
    }

    let count = daysCount || form?.expectedDays || 0;
    if (!count && start && end && !isNaN(start.getTime()) && !isNaN(end.getTime())) {
      const diff = Math.max(1, Math.round((end.getTime() - start.getTime()) / (24 * 3600 * 1000)) + 1);
      count = diff;
    }
    const pad = (n: number) => String(n).padStart(2, '0');
    const days: Day[] = [];
    for (let i = 0; i < count; i++) {
      let dateStr: string | undefined = undefined;
      if (start && !isNaN(start.getTime())) {
        const d = new Date(start);
        d.setDate(d.getDate() + i);
        dateStr = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
      }
      days.push({ day: i + 1, items: [], date: dateStr });
    }
    console.log('[buildDaysFromForm] 生成的 days:', days.map((d, i) => ({ index: i, day: d.day, date: d.date })));
    return { days } as Itinerary;
  } catch (e) {
    return { days: [] } as Itinerary;
  }
};

// 标准化输入数据为统一格式
const normalizeItinerary = (input: any): Itinerary => {
  if (!input) return { days: [] } as Itinerary;

  const days = Array.isArray(input?.days) ? input.days : [];
  
  const normalizeItem = (raw: any): Item => {
    const itemId = raw?.id ? String(raw.id) : genUniqueId();
    const itemType = raw?.type || 'poi';
    const itemTime = raw?.time || undefined;

    const payloadMerged = {
      ...(raw?.payload || {}),
      ...(raw?.inline_payload || {}),
      ...(raw?.custom_payload || {}),
    } as Record<string, any>;

    if (raw?.start_time && !payloadMerged.start_time) payloadMerged.start_time = raw.start_time;
    if (raw?.end_time && !payloadMerged.end_time) payloadMerged.end_time = raw.end_time;

    const payload = Object.keys(payloadMerged).length > 0 ? payloadMerged : undefined;

    const parentEntryId = raw?.parentEntryId ?? raw?.parent_entry_id ?? raw?.meta?.parentEntryId ?? raw?.meta?.parent_entry_id ?? null;
    const childSortValue = raw?.childSort ?? raw?.child_sort ?? raw?.meta?.childSort ?? raw?.meta?.child_sort ?? null;
    const creatorIdValue = raw?.creator_id ?? raw?.creatorId ?? raw?.meta?.creatorId ?? raw?.meta?.creator_id ?? null;
    const createdAtValue = raw?.created_at ?? raw?.createdAt ?? raw?.meta?.createdAt ?? raw?.meta?.created_at ?? null;
    const updatedAtValue = raw?.updated_at ?? raw?.updatedAt ?? raw?.meta?.updatedAt ?? raw?.meta?.updated_at ?? createdAtValue ?? null;
    const creatorProfileValue = raw?.creator_profile ?? raw?.creatorProfile ?? raw?.meta?.creatorProfile ?? null;
    const creatorNameValue = raw?.creator_name ?? raw?.creatorName ?? (creatorProfileValue && (creatorProfileValue.display_name || creatorProfileValue.nickname || creatorProfileValue.name)) ?? null;

    const normalized: any = {
      id: itemId,
      type: itemType,
      title: String(raw?.title || raw?.name || '新景点'),
      note: raw?.note || undefined,
      time: itemTime,
      payload,
      // Preserve original payload snapshots so modal can read from custom_payload directly
      custom_payload: raw?.custom_payload || undefined,
      inline_payload: raw?.inline_payload || undefined,
      card: raw?.card,
      cardTags: raw?.cardTags || raw?.tags || [],
      meta: raw?.meta ? { ...raw.meta } : undefined,
      parentEntryId: parentEntryId || null,
      childSort: typeof childSortValue === 'number' ? childSortValue : null,
      creator_id: creatorIdValue,
      creatorId: creatorIdValue,
      created_at: createdAtValue,
      createdAt: createdAtValue,
      updated_at: updatedAtValue,
      updatedAt: updatedAtValue,
      creator_profile: creatorProfileValue || null,
      creatorProfile: creatorProfileValue || null,
      creator_name: creatorNameValue || undefined,
      creatorName: creatorNameValue || undefined,
    };

    if (normalized.meta) {
      normalized.meta = {
        ...normalized.meta,
        parentEntryId: normalized.parentEntryId,
        childSort: normalized.childSort,
        creatorId: creatorIdValue,
        createdAt: createdAtValue,
        updatedAt: updatedAtValue,
        creatorProfile: creatorProfileValue || null,
      };
    }

    if (Array.isArray(raw?.children) && raw.children.length > 0) {
      normalized.children = raw.children.map((child: any) => normalizeItem(child));
    }

    return normalized as Item;
  };

  const normalizedDays: Day[] = (days || []).map((d: any, idx: number) => ({
    day: d?.day || idx + 1,  // 如果沒有 day，用 index + 1；否則保留原值（即使重複）
    date: d?.date,           // 保留原始日期
    items: Array.isArray(d?.items)
      ? d.items.map((it: any) => normalizeItem(it))
      : [],
  }));

  console.log('[normalizeItinerary] Input days:', days.map(d => ({ day: d?.day, date: d?.date })));
  console.log('[normalizeItinerary] Normalized:', normalizedDays.map((d, i) => ({ index: i, day: d.day, date: d.date })));

  return {
    title: input?.title || input?.name,
    location: input?.location || input?.destination_city || input?.custom_city,
    days: normalizedDays,
  } as Itinerary;
};

// 解析初始数据
const parseInitial = (initialItinerary: any, initialFormData: any, expectedDays?: number): Itinerary => {
  if (initialItinerary) {
    try {
      const parsed = typeof initialItinerary === 'string' ? JSON.parse(initialItinerary) : initialItinerary;
      const normalized = normalizeItinerary(parsed);
      let normalizedStartDate: Date | null = null;
      
      // 填充日期信息：优先从 initialItinerary 中获取，然后从 initialFormData 获取
      try {
        let startDate: Date | null = null;
        
        // 首先尝试从 initialItinerary 中获取日期
        let startRaw = normalized?.start_date || (parsed && parsed.start_date);
        if (!startRaw && initialFormData) {
          // 否则从 initialFormData 获取
          startRaw = initialFormData.startDate || initialFormData.start_date || initialFormData.start;
        }
        
        if (startRaw) {
          // Handle YYYY-MM-DD strings as local date to avoid timezone shifts
          if (typeof startRaw === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(startRaw)) {
            const [y, m, d] = startRaw.split('-').map(Number);
            startDate = new Date(y, m - 1, d);
          } else {
            startDate = new Date(startRaw);
          }
        }
        
        if (startDate && !isNaN(startDate.getTime()) && normalized && Array.isArray(normalized.days)) {
          normalizedStartDate = startDate;
          const pad = (n: number) => String(n).padStart(2, '0');
          normalized.days = normalized.days.map((d, i) => {
            // Always recalculate date based on start_date + index to ensure consistency
            // Use d.day if available (1-based) to calculate offset, otherwise fallback to array index
            const dayOffset = (typeof d.day === 'number' && d.day > 0) ? (d.day - 1) : i;
            
            const dt = new Date(startDate!);
            dt.setDate(dt.getDate() + dayOffset);
            const dateStr = `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
            return { ...d, date: dateStr };
          });
        }
      } catch (e) {
        // 忽略日期合并错误
      }

      return ensureDaysCount(normalized || { days: [] } as Itinerary, expectedDays, normalizedStartDate);
    } catch (e) {
      console.warn('Failed to parse initialItinerary', e);
    }
  }

  if (initialFormData) {
    const built = buildDaysFromForm(initialFormData, initialFormData.expectedDays || expectedDays);
    let startRaw = initialFormData.startDate || initialFormData.start_date || initialFormData.start;
    const startDate = startRaw ? new Date(startRaw) : null;
    return ensureDaysCount(built || { days: [] } as Itinerary, expectedDays, startDate);
  }

  return ensureDaysCount({ days: [] } as Itinerary, expectedDays);
};

const computeNextItemTime = (items: any[]) => {
  try {
    if (!items || items.length === 0) return '09:00';
    
    // 提取所有有效的时间
    const times = items
      .map(i => (i && i.time) ? i.time : null)
      .filter(Boolean);
    
    if (times.length === 0) return '09:00';
    
    // 转换为分钟数并排序，找到最大的时间
    const timeInMinutes = times.map(t => {
      const parts = String(t).split(':');
      const hh = parseInt(parts[0] || '9', 10);
      const mm = parseInt(parts[1] || '0', 10);
      return (isNaN(hh) ? 9 : hh) * 60 + (isNaN(mm) ? 0 : mm);
    });
    
    const maxMinutes = Math.max(...timeInMinutes);
    let nextHour = Math.floor(maxMinutes / 60) + 1;
    
    if (nextHour >= 24) nextHour = 23;
    return `${String(nextHour).padStart(2, '0')}:00`;
  } catch (e) {
    return '09:00';
  }
};

const ensureItemTimes = (it: Itinerary) => {
  try {
    const clone: Itinerary = JSON.parse(JSON.stringify(it || { days: [] }));
    (clone.days || []).forEach((d: any) => {
      (d.items || []).forEach((itItem: any, idx: number) => {
        if (!itItem.time) itItem.time = computeNextItemTime(d.items.slice(0, idx));
      });
    });
    return clone;
  } catch (e) {
    return it;
  }
};

export const useItineraryEditor = (opts: UseItineraryEditorOptions = {}) => {
  const { initialItinerary, initialFormData, expectedDays } = opts;
  const [itinerary, setItinerary] = useState<Itinerary>(() => parseInitial(initialItinerary, initialFormData, expectedDays));
  const [selectedDayIndex, setSelectedDayIndex] = useState<number>(0);
  const [processingIds, setProcessingIds] = useState<string[]>([]);
  const formDataRef = useRef(initialFormData);

  const isProcessing = (id?: string) => id ? processingIds.includes(String(id)) : false;
  const addProcessing = (id: string) => setProcessingIds(prev => prev.includes(id) ? prev : [...prev, id]);
  const removeProcessing = (id: string) => setProcessingIds(prev => prev.filter(i => i !== id));

  // 根据 initialFormData 和 day 索引计算日期
  const computeDateForDayWithFormData = (dayIndex: number): string | undefined => {
    try {
      const form = formDataRef.current;
      if (!form) return undefined;
      const startRaw = form.startDate || form.start_date || form.start;
      if (!startRaw) return undefined;
      
      let start: Date;
      if (typeof startRaw === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(startRaw)) {
         const [y, m, d] = startRaw.split('-').map(Number);
         start = new Date(y, m - 1, d);
      } else {
         start = new Date(startRaw);
      }

      if (isNaN(start.getTime())) return undefined;
      const targetDate = new Date(start);
      targetDate.setDate(targetDate.getDate() + dayIndex);
      const pad = (n: number) => String(n).padStart(2, '0');
      return `${targetDate.getFullYear()}-${pad(targetDate.getMonth() + 1)}-${pad(targetDate.getDate())}`;
    } catch (e) {
      return undefined;
    }
  };

  const addDay = () => {
    const days = [...(itinerary.days || [])];
    const newDayNum = days.length + 1;
    const newDate = computeDateForDayWithFormData(days.length);
    // 確保 day 號碼始終 = index + 1，防止重複
    days.push({ day: newDayNum, items: [], date: newDate });
    const updated = { ...itinerary, days };
    setItinerary(updated);
    setSelectedDayIndex(days.length - 1);
    console.log('[addDay] 新增第', newDayNum, '天，目前總共', days.length, '天');
  };

  const addItem = (dayIndex?: number) => {
    const di = typeof dayIndex === 'number' ? dayIndex : selectedDayIndex || 0;
    const days = [...(itinerary.days || [])];
    
    // 補齊缺少的天數，保持 day 號碼正確（每個 day 號必須等於其 index + 1）
    while (days.length <= di) {
      const idx = days.length;
      const newDate = computeDateForDayWithFormData(idx);
      days.push({ day: idx + 1, items: [], date: newDate });
      console.log('[addItem] 自動添加第', idx + 1, '天以支援添加卡片到第', di + 1, '天');
    }
    
    const currentItems = [...(days[di].items || [])];
    const time = computeNextItemTime(currentItems);
    const newItem: Item = { id: genUniqueId(), type: 'poi', title: '新景点', time, note: '' };
    days[di].items = [...currentItems, newItem];
    
    // 驗證 day 編號正確性
    days.forEach((d, idx) => {
      if (d.day !== idx + 1) {
        console.error('[addItem] day 編號不匹配！', { expected: idx + 1, actual: d.day });
        d.day = idx + 1; // 強制修復
      }
    });
    
    const updated = { ...itinerary, days };
    setItinerary(updated);
  };

  const setDayItems = (dayIndex: number, items: Item[]) => {
    const days = [...(itinerary.days || [])];
    while (days.length <= dayIndex) {
      const idx = days.length;
      const newDate = computeDateForDayWithFormData(idx);
      days.push({ day: idx + 1, items: [], date: newDate });
    }
    days[dayIndex] = { ...(days[dayIndex] || { day: dayIndex + 1, items: [] }), items } as Day;
    setItinerary({ ...itinerary, days });
  };

  const updateItem = (dayIndex: number, itemIndex: number, patch: Partial<Item>) => {
    const days = [...(itinerary.days || [])];
    while (days.length <= dayIndex) {
      const idx = days.length;
      const newDate = computeDateForDayWithFormData(idx);
      days.push({ day: idx + 1, items: [], date: newDate });
    }
    const items = [...(days[dayIndex].items || [])];
    const old = items[itemIndex] as Item | undefined;
    if (!old) return;
    items[itemIndex] = { ...old, ...patch } as Item;
    days[dayIndex] = { ...(days[dayIndex] || { day: dayIndex + 1 }), items } as Day;
    setItinerary({ ...itinerary, days });
  };

  const deleteItem = (dayIndex: number, itemIndex: number) => {
    const days = [...(itinerary.days || [])];
    if (!days[dayIndex]) return;
    const items = [...(days[dayIndex].items || [])];
    items.splice(itemIndex, 1);
    days[dayIndex] = { ...(days[dayIndex] || { day: dayIndex + 1 }), items } as Day;
    setItinerary({ ...itinerary, days });
  };

  const setItemTime = (dayIndex: number, itemIndex: number, time: string) => updateItem(dayIndex, itemIndex, { time } as Partial<Item>);

  const saveAsText = () => {
    const normalized = ensureItemTimes(itinerary);
    setItinerary(normalized);
    const text = JSON.stringify(normalized);
    return { normalized, text };
  };

  return {
    itinerary,
    setItinerary,
    selectedDayIndex,
    setSelectedDayIndex,
    addDay,
    addItem,
    setDayItems,
    updateItem,
    deleteItem,
    setItemTime,
    isProcessing,
    addProcessing,
    removeProcessing,
    saveAsText,
  };
};

export default useItineraryEditor;
